// Starting a Unity port from the dashboard: the game and branch to port, the confirmation, and the prompt the session opens with.

import React, { useEffect, useState } from "react";
import _ from "lodash";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { listBranches, listGames } from "../../modules/levelApi";
import { FIELD_LABEL, PANEL_BG } from "./panelPalette";

const useStyles = makeStyles({
  row: { display: "flex", alignItems: "flex-end", gap: "12px", flexWrap: "wrap", marginBottom: "10px" },
  label: { color: FIELD_LABEL, fontSize: "11px", lineHeight: 1.4 },
  hint: { fontSize: "12px", color: FIELD_LABEL, opacity: 0.85, marginTop: "8px", lineHeight: 1.6 },
  failure: { marginTop: "8px", fontSize: "12px", color: "#ef9a9a" },
  summary: { fontSize: "13px", color: "#ddd", lineHeight: 1.7 },
  strong: { color: "#fff" },
  ported: { color: FIELD_LABEL, fontSize: "12px", marginLeft: "8px" },
});

const studioOf = (game) => game.studio || "Fugo";

function formatBytes(bytes) {
  const value = Number(bytes) || 0;
  if (value >= 1024 ** 3) return `${(value / 1024 ** 3).toFixed(1)} GB`;
  if (value >= 1024 ** 2) return `${Math.round(value / 1024 ** 2)} MB`;
  return `${Math.round(value / 1024)} KB`;
}

function formatWhen(iso) {
  if (!iso) return "";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  return new Date(iso).toLocaleDateString();
}

export function convertPrompt({ game, branch, instructions }) {
  const lines = [
    `Port the Unity game "${game.name}" (${game.path}), branch ${branch}, into a PlayEngine playable.`,
    "",
    "Read playengine/.claude/skills/unity-port/SKILL.md in full before you start, then references/lessons.md.",
    "",
    "Get the source with:",
    `  node scripts/fetchUnityRepo.js ${game.path} --ref ${branch}`,
    "",
    "The script writes an inventory to .unity-port-fetch.json in the checkout; that inventory is step 1 of the skill. Art is not checked out by default — add the folders you need with --add <path>. The repository token comes from the environment: never ask for it, print it or write it anywhere.",
    "",
    `Write the source into the snapshot's metadata — metadata.sourceProject "${game.path}", metadata.sourceBranch "${branch}" and metadata.sourceCommit from the inventory — so a later update to this game can be applied as a difference instead of a fresh port.`,
    "",
    "The port has a second deliverable: the game's levels must be selectable from the dashboard afterwards, because an ad may be built from level 1 or level 300. Write that game's level toolbox script to snapshots/toolboxScripts/<game>.toolbox.js and set metadata.toolbox on the snapshot. The contract, the ctx surface and a skeleton are in this flow's CLAUDE.md under \"Port'un ikinci ciktisi\". If the game has no level data at all, say so in the report instead.",
    "",
    "Finish with a short report, in this order: which snapshot file you produced, what of the game it covers, what you deliberately left out and why, what you could not port and what blocked it, and anything you had to guess. Name the parts that are missing — a port that reads as complete when it is not costs more than one that says what is missing.",
  ];
  if (game.game) {
    lines.push("", `This game already has a playable (${game.game}). Check what exists before building anything new.`);
  }
  const extra = String(instructions || "").trim();
  if (extra) lines.push("", "Also:", extra);
  return lines.join("\n");
}

export function ConvertSessionFields({ baseID, disabled, value, onChange }) {
  const classes = useStyles();
  const [games, setGames] = useState([]);
  const [areGamesLoading, setAreGamesLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [areBranchesLoading, setAreBranchesLoading] = useState(false);
  const [failure, setFailure] = useState(null);

  const path = _.get(value, "game.path") || "";
  const branch = _.get(value, "branch") || "";
  const detail = _.get(value, "detail") || null;

  useEffect(() => {
    if (!baseID) return undefined;

    let isCurrent = true;
    setAreGamesLoading(true);
    listGames(baseID)
      .then((response) => {
        if (!isCurrent) return;
        if (!response.ok) {
          setFailure(_.get(response, "error.error") || response.error || "The game list could not be read.");
          return;
        }
        setGames(_.get(response, "data.games", []));
      })
      .finally(() => {
        if (isCurrent) setAreGamesLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [baseID]);

  useEffect(() => {
    if (!baseID || !path) return undefined;

    let isCurrent = true;
    setFailure(null);
    setAreBranchesLoading(true);
    setBranches([]);
    listBranches(baseID, path)
      .then((response) => {
        if (!isCurrent) return;
        if (!response.ok) {
          setFailure(_.get(response, "error.error") || response.error || "The branch list could not be read.");
          return;
        }
        const names = _.get(response, "data.branches", []);
        setBranches(names);
        onChange({
          ...value,
          branch: _.get(response, "data.defaultBranch") || names[0] || "",
          detail: _.get(response, "data") || null,
        });
      })
      .finally(() => {
        if (isCurrent) setAreBranchesLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [baseID, path]);

  return (
    <>
      <div className={classes.row}>
        <Autocomplete
          style={{ minWidth: "220px", flex: 1 }}
          size="small"
          options={games}
          groupBy={studioOf}
          getOptionLabel={(option) => option.name || ""}
          getOptionSelected={(option, picked) => option.path === picked.path}
          filterOptions={(candidates, { inputValue }) => {
            const query = String(inputValue || "").trim().toLowerCase();
            if (!query) return candidates;
            return candidates.filter((option) =>
              `${option.name} ${studioOf(option)} ${option.path}`.toLowerCase().includes(query)
            );
          }}
          value={games.find((item) => item.path === path) || null}
          onChange={(_event, picked) => onChange({ game: picked || null, branch: "", detail: null })}
          disabled={disabled || areGamesLoading}
          renderOption={(option) => (
            <span>
              {option.name}
              <span className={classes.ported}>{studioOf(option)}</span>
              {option.game ? <span className={classes.ported}>already a playable</span> : null}
            </span>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Game"
              InputLabelProps={{ ...params.InputLabelProps, shrink: true, style: { color: FIELD_LABEL } }}
              placeholder={areGamesLoading ? "Loading…" : `Search ${games.length || ""} games…`}
            />
          )}
        />

        <Autocomplete
          style={{ minWidth: "220px", flex: 1 }}
          size="small"
          options={branches}
          value={branch || null}
          onChange={(_event, picked) => onChange({ ...value, branch: picked || "" })}
          disabled={disabled || !path || areBranchesLoading}
          disableClearable
          renderInput={(params) => (
            <TextField
              {...params}
              label="Branch"
              InputLabelProps={{ ...params.InputLabelProps, shrink: true, style: { color: FIELD_LABEL } }}
              placeholder={areBranchesLoading ? "Loading…" : "Pick a branch"}
            />
          )}
        />
      </div>

      {detail ? (
        <Typography className={classes.hint}>
          Repository {formatBytes(detail.sourceBytes)}
          {detail.assetBytes ? ` + ${formatBytes(detail.assetBytes)} LFS` : ""} · {branches.length} branches
          {detail.defaultBranch ? ` · default ${detail.defaultBranch}` : ""} · {detail.commits} commits · last change{" "}
          {formatWhen(detail.lastActivityAt)}
        </Typography>
      ) : null}

      {areGamesLoading && !games.length ? (
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "8px" }}>
          <CircularProgress size={16} />
          <Typography style={{ fontSize: "12px", color: FIELD_LABEL }}>Reading the game list…</Typography>
        </div>
      ) : null}

      {failure ? <Typography className={classes.failure}>{failure}</Typography> : null}
    </>
  );
}

export function ConvertConfirmDialog({ open, game, branch, detail, instructions, onBack, onConfirm }) {
  const classes = useStyles();
  if (!game) return null;

  return (
    <Dialog open={open} onClose={onBack} maxWidth="xs" fullWidth PaperProps={{ style: { backgroundColor: PANEL_BG } }}>
      <DialogTitle style={{ fontSize: "1.4rem", padding: "16px 16px 8px" }}>Convert this game?</DialogTitle>
      <DialogContent>
        <Typography className={classes.summary}>
          <span className={classes.strong}>{game.name}</span> will be ported from{" "}
          <span className={classes.strong}>{game.path}</span> on branch <span className={classes.strong}>{branch}</span>.
          are you sure ?
        </Typography>
        {detail ? (
          <Typography className={classes.hint}>
            The repository is {formatBytes(detail.sourceBytes)}
            {detail.assetBytes ? ` plus ${formatBytes(detail.assetBytes)} of Git LFS art` : ""}. Only the readable
            source is checked out — scripts, scenes, prefabs — and art folders are fetched one at a time, as the port
            needs them.
          </Typography>
        ) : null}
        <Typography className={classes.hint}>
          The session starts and runs the unity-port procedure. Nothing is overwritten — the result arrives as a new
          snapshot file, and you can stop the session at any point.
          {String(instructions || "").trim() ? " Your extra instruction goes with it." : ""}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onBack}>Back</Button>
        <Button color="primary" variant="contained" onClick={onConfirm}>
          Convert
        </Button>
      </DialogActions>
    </Dialog>
  );
}
